import { useCallback, useEffect, useRef } from "react";

/**
 * @param {Object} options
 * @param {string} options.siteKey - reCAPTCHA site key
 * @param {boolean} [options.enabled=true] - Whether reCAPTCHA is enabled
 * @param {'invisible'|'normal'|'compact'} [options.size='invisible'] - Widget size
 * @param {'light'|'dark'} [options.theme='light'] - Widget theme
 * @param {Function} options.onSuccess - Callback when verification succeeds
 * @param {Function} options.onError - Callback when verification fails
 */
export const useRecaptcha = ({
  siteKey,
  enabled = true,
  size = "invisible",
  theme = "light",
  onSuccess,
  onError,
}) => {
  const recaptchaRef = useRef(null);
  const recaptchaWidgetId = useRef(null);
  const cleanupInterval = useRef(null);

  // Initialize reCAPTCHA when component mounts
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const renderWidget = () => {
      // Only render if we have a ref and haven't already rendered
      if (recaptchaRef.current && recaptchaWidgetId.current === null) {
        try {
          recaptchaWidgetId.current = window.grecaptcha.render(
            recaptchaRef.current,
            {
              sitekey: siteKey,
              size: size,
              callback: onSuccess,
              "error-callback": onError,
              theme: theme,
            }
          );
        } catch (error) {
          // If widget is already rendered, reset and try again
          if (error.message.includes("already been rendered")) {
            recaptchaWidgetId.current = null;
            // Clear the element and try again
            if (recaptchaRef.current) {
              recaptchaRef.current.innerHTML = "";
              try {
                recaptchaWidgetId.current = window.grecaptcha.render(
                  recaptchaRef.current,
                  {
                    sitekey: siteKey,
                    size: size,
                    callback: onSuccess,
                    "error-callback": onError,
                    theme: theme,
                  }
                );
              } catch (retryError) {
                console.warn("Failed to re-render reCAPTCHA:", retryError);
              }
            }
          } else {
            console.warn("reCAPTCHA render error:", error);
          }
        }
      }
    };

    // Use grecaptcha.ready() to ensure the API is fully loaded
    if (window.grecaptcha && window.grecaptcha.ready) {
      window.grecaptcha.ready(renderWidget);
    } else {
      // Fallback for when grecaptcha.ready is not available
      cleanupInterval.current = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          clearInterval(cleanupInterval.current);
          window.grecaptcha.ready(renderWidget);
        }
      }, 100);
    }

    // Cleanup function
    return () => {
      if (cleanupInterval.current) {
        clearInterval(cleanupInterval.current);
      }
    };
  }, [enabled, siteKey, size, theme, onSuccess, onError]);

  // Execute reCAPTCHA verification
  const executeRecaptcha = useCallback(() => {
    if (enabled && window.grecaptcha && recaptchaWidgetId.current !== null) {
      window.grecaptcha.execute(recaptchaWidgetId.current);
      return true;
    }
    return false;
  }, [enabled]);

  // Reset reCAPTCHA
  const resetRecaptcha = useCallback(() => {
    if (window.grecaptcha && recaptchaWidgetId.current !== null) {
      window.grecaptcha.reset(recaptchaWidgetId.current);
    }
  }, []);

  // Get reCAPTCHA response token
  const getRecaptchaResponse = useCallback(() => {
    if (window.grecaptcha && recaptchaWidgetId.current !== null) {
      return window.grecaptcha.getResponse(recaptchaWidgetId.current);
    }
    return null;
  }, []);

  return {
    recaptchaRef,
    executeRecaptcha,
    resetRecaptcha,
    getRecaptchaResponse,
    isReady: recaptchaWidgetId.current !== null,
  };
};
